<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          <div class="header-title" v-if="!editCurrentPanel">
            Person Type Details
          </div>
          <div v-else>
            Edit Person Type Details
          </div>
        </template>
        <template #actions
          v-if="!editCurrentPanel && !loading && !loadingError">
          <ul>
            <li v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')"
              class="p-narrow no-border" style="padding-right: 0">
              <div class="list-content">
                <a role="button" tabindex="0"
                  @keyup.enter="editPanel()"
                  title="Edit"
                  @click="editPanel()">
                  <font-awesome-icon :icon="['far', 'edit']" size="lg"/>
                </a>
              </div>
            </li>
            <li class="p-narrow no-border">
              <div class="list-content">
                <a role="button" tabindex="0"
                  @keyup.enter="goBackToSubmissionSpecifications()"
                  title="Go Back to Previous Page"
                  @click="goBackToSubmissionSpecifications()"
                  v-if="!editCurrentPanel"
                >
                  <font-awesome-icon :icon="['far', 'angle-double-left']" size="2x"/>
                </a>
              </div>
            </li>
          </ul>
        </template>
      </EsHeader>
    </div>
    <es-loader v-if="loading" style="margin: 5rem"/>
    <es-exception
      v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
      margin="0"
      style="padding: 5rem"
    />
    <div v-else class="content">
      <es-form style="padding: 1.15rem 0rem">
        <template #es-form-header>
          <div class="header-container">
            <div class="title is-6">
              {{ personType.name }} Person Type
            </div>
          </div>
        </template>
        <template #es-form>
          <table role="presentation">
            <tbody>
              <tr>
                <td class="property has-text-grey top-row no-border top-padding">
                  Type Name (Code)
                </td>
                <td class="top-row no-border">
                  <div class="control">
                    <input
                      class="input is-static"
                      type="text"
                      :readonly="!editCurrentPanel"
                      :value="personType.name+' ('+personType.code+')'"
                      aria-label="Type Name (Code)"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td class="property has-text-grey no-border">
                  Category
                </td>
                <td class="no-border">
                  <div class="control">
                    <input
                      class="input is-static"
                      type="text"
                      :readonly="!editCurrentPanel"
                      :value="personTypeCategoryOptions.find(
                        item => item.code === personType.category
                      ).description"
                      aria-label="Category"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td class="property has-text-grey no-border">
                  Concurrent Location Assignment
                </td>
                <td class="no-border">
                  <div class="control">
                    <input
                      class="input is-static"
                      :class="{ 'is-readonly': editCurrentPanel }"
                      type="text"
                      readonly
                      :value="booleanOptions.find(
                        item => item.code === personType.concurrentLocAssign
                      ).yesNo"
                      aria-label="Concurrent Location Assignment"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td class="property has-text-grey no-border">
                  Status
                </td>
                <td class="no-border">
                  <div class="control">
                    <div
                      class="buttons has-addons"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form"
                        :disabled="personType.defaultType"
                        v-for="(option, index) in booleanOptions"
                        :key="index"
                        @click="personType.status = option.code"
                        :class="{ 'is-active': personType.status === option.code }"
                      >
                        {{ option.activeInactive }}
                      </button>
                    </div>
                    <input
                      v-else
                      class="input"
                      :class="{ 'is-static': !editCurrentPanel }"
                      type="text"
                      :readonly="!editCurrentPanel"
                      :value="booleanOptions.find(
                        item => item.code === personType.status
                      ).activeInactive"
                      aria-label="Status"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td class="property has-text-grey no-border">
                  Default
                </td>
                <td class="no-border">
                  <div class="control">
                    <input
                      class="input is-static"
                      type="text"
                      readonly
                      :value="booleanOptions.find(
                        item => item.code === personType.defaultType
                      ).yesNo"
                      aria-label="Default"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </es-form>
      <div
        v-if="editCurrentPanel"
        class="is-flex flex-justify--end flex-align--center"
        style="margin-right: 2rem; padding-bottom: 2rem"
      >
        <button
          class="button button-link has-underline"
          @click.prevent="cancelPanel()"
          style="margin-right: 1rem"
        >
          Cancel
        </button>
        <button
          class="button is-primary"
          @click.prevent="updatePanel()"
        >
          Update
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleLeft,
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleLeft,
  faEdit,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
  },
  props: {
    receivedAllPersonTypes: {
      type: Array,
    },
    receivedPersonType: {
      type: Object,
    },
  },
  watch: {
    currentDefault() {
      if (this.currentDefault === true) {
        this.personType.status = true;
      }
    },
    currentStatus() {
      if (this.currentStatus === false) {
        this.personType.defaultType = false;
      }
    },
  },
  data() {
    return this.initialData();
  },
  created() {
    this.onLoad();
    this.getPersonType();
    this.booleanOptions = [
      {
        code: true,
        yesNo: 'Yes',
        activeInactive: 'Active',
      },
      {
        code: false,
        yesNo: 'No',
        activeInactive: 'Inactive',
      },
    ];
  },
  mounted() {
    this.updateCache();
  },
  computed: {
    ...mapGetters('lookups', [
      'allPersonTypeCategories',
    ]),
    otherPersonTypes() {
      const index = this.receivedAllPersonTypes.indexOf(this.receivedPersonType);
      const temp = this.receivedAllPersonTypes;
      if (index > -1) {
        temp.splice(index, 1);
      }
      return temp;
    },
    currentDefault() {
      return this.personType.defaultType;
    },
    currentStatus() {
      return this.personType.status;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypeCategories',
    ]),
    cancelPanel() {
      this.undoForm();
      this.hideNotification();
      this.editCurrentPanel = false;
    },
    checkForFormChanges() {
      return this.compareObjects(this.cachedPersonType, this.personType);
    },
    compareObjects(o, n) {
      return JSON.stringify(o) !== JSON.stringify(n);
    },
    editPanel() {
      this.editCurrentPanel = !this.editCurrentPanel;
      this.hideNotification();
      this.$emit('toggleAddExactMatchRule');
    },
    getPersonType() {
      this.personType = this.receivedPersonType;
      return this.personType;
    },
    goBackToSubmissionSpecifications() {
      this.hideNotification();
      this.$emit('showSubmissionSpecifications');
    },
    initialData() {
      return {
        cachedPersonType: {},
        editCurrentPanel: false,
        loading: false,
        loadingError: false,
        personType: {},
        personTypeCategoryOptions: [],
      };
    },
    async onLoad() {
      try {
        this.loading = true;
        this.loadingError = false;
        this.personTypeCategoryOptions = await this.loadPersonTypeCategories();
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    undoForm() {
      this.personType = JSON.parse(JSON.stringify(this.cachedPersonType));
    },
    updateCache() {
      this.cachedPersonType = JSON.parse(JSON.stringify(this.personType));
    },
    async updateOtherPersonTypes() {
      // Check if there is any change in defaultType selection and do nothing if NO change
      if (!this.compareObjects(this.cachedPersonType.defaultType, this.personType.defaultType)) {
        this.editCurrentPanel = false;
        this.hideNotification();
      } else {
        // If there was a change, update all personType's defaultType
        this.otherPersonTypes.forEach(async (eachPersonType) => {
          try {
            this.loading = true;
            this.loadingError = false;
            // Update all other personType's defaultType to FALSE
            // when user sets current personType's defaultType to TRUE
            if (this.personType.defaultType === true) {
              eachPersonType.defaultType = false; // eslint-disable-line no-param-reassign
            }
            await axios({
              method: 'put',
              url: `/person-types/${eachPersonType.id}`,
              data: eachPersonType,
            });
          } catch (err) {
            this.loadingError = true;
          } finally {
            this.loading = false;
          }
        });
      }
    },
    async updatePanel() {
      const changes = this.checkForFormChanges();
      if (!changes) {
        this.editCurrentPanel = false;
        this.hideNotification();
        return;
      }
      try {
        this.loading = true;
        this.loadingError = false;
        let newData = {};
        if (this.personType.status === false) {
          newData = this.otherPersonTypes.find(s => s.status === true);
          if (!newData) {
            this.showNotification('danger', ['Atleast one person type should be Active !!!']);
            return;
          }
        }
        if (this.personType.defaultType === false) {
          newData = this.otherPersonTypes.find(s => s.defaultType === true);
          if (!newData) {
            this.showNotification('danger', ['Atleast one person type should be Default !!!']);
            return;
          }
        }
        await axios({
          method: 'put',
          url: `/person-types/${this.personType.id}`,
          data: this.personType,
        });
        this.updateOtherPersonTypes();
        this.updateCache();
        this.showNotification('success', ['The Person Type has been updated successfully.']);
        this.editCurrentPanel = false;
        this.onLoad();
      } catch (error) {
        this.loadingError = true;
        this.apiMsg = 'There was an issue. Please try again or contact your administrator.';
        this.showNotification('danger', [this.apiMsg]);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table th
    vertical-align: middle
  table td
    vertical-align: middle
    padding-left: 0
    &:first-child
      width: 30%
  .header
    display: flex
    align-items: center
  .no-border
    border: 0
  .property
    padding-left: 0
    font-size: 12px
    width: 70%
  .top-padding
    padding-top: 1rem
  .content
    overflow-x: auto
</style>
